import {
  Box,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CloseIcon } from "components";
import { FileIcon } from "components/FileIcon";
import { memo } from "react";
import type { AttachmentFileData } from "shared/types";

// ─── Types ───────────────────────────────────────────────────────────

interface ChatAttachmentPreviewProps {
  files: AttachmentFileData[];
  onRemove: (key: string) => void;
  onClearAll: () => void;
}

// ─── Component ───────────────────────────────────────────────────────

export const ChatAttachmentPreview = memo(
  ({ files, onRemove, onClearAll }: ChatAttachmentPreviewProps) => {
    const theme = useTheme();

    if (files.length === 0) return null;

    return (
      <Stack sx={{ padding: "8px 12px 0" }}>
        {/* File chips */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          {files.map((file) => {
            const isError = file.status === "Error";
            const isUploading = file.status === "Uploading";

            return (
              <Stack
                key={file.key}
                sx={{
                  minWidth: "160px",
                  maxWidth: "220px",
                  borderRadius: "6px",
                  border: `1px solid ${
                    isError
                      ? theme.palette.error.main
                      : theme.palette.neutral["010"]
                  }`,
                  backgroundColor: theme.palette.neutral.main,
                  overflow: "hidden",
                }}
              >
                {/* Top: Icon + Name + Close */}
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 8px",
                  }}
                >
                  <Box sx={{ flexShrink: 0, display: "flex" }}>
                    <FileIcon type={file.type} width="20" height="20" />
                  </Box>
                  <Typography
                    variant="body-small"
                    sx={{
                      color: theme.palette.lightBg.high,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {file.name}
                  </Typography>
                  <IconButton
                    aria-label={`Remove ${file.name}`}
                    onClick={() => onRemove(file.key)}
                    size="small"
                    sx={{
                      padding: "2px",
                      flexShrink: 0,
                      color: theme.palette.lightBg.low,
                    }}
                  >
                    <CloseIcon sx={{ width: 14, height: 14 }} />
                  </IconButton>
                </Stack>

                {/* Bottom: Error message or progress bar */}
                {isError && file.errorMessage && (
                  <Typography
                    variant="caption-2"
                    sx={{
                      color: theme.palette.error.main,
                      px: "8px",
                      pb: "4px",
                    }}
                  >
                    {file.errorMessage}
                  </Typography>
                )}
                {isUploading && (
                  <LinearProgress
                    variant="determinate"
                    value={file.progress}
                    color="success"
                    sx={{ height: "3px" }}
                  />
                )}
                {isError && (
                  <LinearProgress
                    variant="determinate"
                    value={100}
                    color="error"
                    sx={{ height: "3px" }}
                  />
                )}
              </Stack>
            );
          })}
        </Box>

        {/* Clear All link */}
        <Typography
          variant="caption-2"
          onClick={onClearAll}
          sx={{
            color: theme.palette.primary.main,
            cursor: "pointer",
            mt: "6px",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          Clear All
        </Typography>
      </Stack>
    );
  },
);
